import React, { useCallback, useMemo, useState } from 'react';
import { usePromiseTracker } from 'react-promise-tracker';
import { Button } from 'rsuite';

import { LoadingMenu } from '../../../../../../../components/LoadingSpinner';
import SelectPill from '../../../../../../../components/SelectPill';
import { PRODUCT_REFERENCE_MAX_SELECTED, PRODUCT_REFERENCE_TRACKER_AREA } from '../../../constants';
import { useProductReference, type ProductReferenceOption } from '../../../hooks/useProductReference';
import styles from './ProductSelector.module.scss';

const buildPlaceholder = (value: string[], options: ProductReferenceOption[]) => {
  if (value.length === 0) return 'Produto referência';
  return `Produtos selecionados`;
};

const mergeCachedAndSearchOptions = <T extends { value: unknown }>(cachedOptions: T[] = [], searchOptions: T[] = []) => {
  const combined = [...cachedOptions];
  searchOptions.forEach(option => {
    if (!combined.find(cached => cached.value === option.value)) {
      combined.push(option);
    }
  });
  return combined;
};

export const useProductSelectOptions = () => {
  const [cachedOptions, setCachedOptions] = useState<ProductReferenceOption[]>([]);
  const productReference = useProductReference();

  const productSelectOptions = useMemo(
    () => mergeCachedAndSearchOptions(cachedOptions, productReference.productOptions),
    [cachedOptions, productReference.productOptions],
  );

  return {
    ...productReference,
    productSelectOptions,
    setCachedOptions,
  };
};

type ProductSelectorProps = {
  value: string[];
  onChange: (value: string[]) => void;
};

export const ProductSelector = ({ value, onChange }: ProductSelectorProps) => {
  const {
    productSelectOptions,
    setCachedOptions,
    handleLoadMore,
    handleOpen,
    handleProductReferenceSearch,
    handleProductReferenceChange,
    maxSize,
    productOptions,
  } = useProductSelectOptions();
  const { promiseInProgress: isLoadingProducts } = usePromiseTracker({ area: PRODUCT_REFERENCE_TRACKER_AREA });

  const handleReferenceProductChange = useCallback(
    (selectedValues: string[]) => {
      onChange(selectedValues);
      handleProductReferenceChange(selectedValues);
      setCachedOptions(productSelectOptions.filter(option => selectedValues.includes(option.value)));
    },
    [handleProductReferenceChange, onChange, productSelectOptions, setCachedOptions],
  );

  const handleReferenceProductClean = useCallback(() => {
    onChange([]);
    handleProductReferenceChange([]);
    setCachedOptions([]);
  }, [handleProductReferenceChange, onChange, setCachedOptions]);

  const disabledItemValues = useMemo(() => {
    if (value.length < PRODUCT_REFERENCE_MAX_SELECTED) return [];
    return productSelectOptions.map(option => option.value).filter(optionValue => !value.includes(optionValue));
  }, [productSelectOptions, value]);

  return (
    <>
      {/* @ts-ignore */}
      <SelectPill
        id="check-picker-pill"
        type="check"
        placeholder={buildPlaceholder(value, productSelectOptions)}
        data={productSelectOptions}
        value={value}
        className={value.length > 0 ? styles.productSelectorSelected : undefined}
        onSelect={handleReferenceProductChange}
        onClean={handleReferenceProductClean}
        onSearch={handleProductReferenceSearch}
        onOpen={handleOpen}
        disabledItemValues={disabledItemValues}
        filter={false}
        searchable
        cleanable
        locale={{
          noResultsText: isLoadingProducts ? ' ' : 'Nenhum item encontrado',
          searchPlaceholder: 'Busque por um produto',
        }}
        menuClassName="elasticidade-product-picker-menu"
        renderMenu={(menu: React.ReactNode) => (
          <span>
            <LoadingMenu area={PRODUCT_REFERENCE_TRACKER_AREA} />
            {menu}
          </span>
        )}
        renderExtraFooter={() => (
          <div className={styles.productMenuFooter}>
            <Button appearance="ghost" onClick={handleLoadMore} disabled={productOptions.length >= maxSize}>
              Carregar mais
            </Button>
          </div>
        )}
      />
    </>
  );
};
